/**
 * Fulfillment checklist: match scanned codes to cart lines or deliveryNote SKUs.
 */

/**
 * Normalize a hardware/barcode scan for line matching (e.g. IKEA EANs append digits after the 8-digit article).
 * Only shortens when the scan is entirely numeric and longer than `maxDigits` — then keeps the leading digits.
 * Alphanumeric or punctuated SKUs are left as trimmed text so variants can still be derived in `expandMatchVariants`.
 *
 * @param {unknown} rawCode
 * @param {number} [maxDigits=8]
 * @returns {string}
 */
export function normalizePackingScan(rawCode, maxDigits = 8) {
  if (rawCode == null) return '';
  const trimmed = String(rawCode).trim();
  if (!trimmed) return '';
  if (/^\d+$/.test(trimmed) && trimmed.length > maxDigits) {
    return trimmed.slice(0, maxDigits);
  }
  return trimmed;
}

export function expandMatchVariants(raw) {
  if (raw == null || raw === '') return [];
  const t = String(raw).trim();
  if (!t) return [];
  const variants = new Set();
  const add = (v) => {
    if (v !== undefined && v !== null && String(v).length > 0) {
      variants.add(String(v));
    }
  };
  add(t);
  add(t.toLowerCase());
  add(t.replace(/^s/i, ''));
  const digits = t.replace(/\D/g, '');
  add(digits);
  if (digits.length > 0) {
    const stripped = digits.replace(/^0+/, '');
    add(stripped || '0');
    if (digits.length >= 8) {
      add(digits.slice(-8));
      add(digits.slice(-8).replace(/^0+/, '') || '0');
    }
    if (digits.length >= 12) {
      add(digits.slice(0, 12));
    }
  }
  return [...variants];
}

export function mergeVariantKeys(...rawKeys) {
  const set = new Set();
  rawKeys.flat().forEach((k) => {
    expandMatchVariants(k).forEach((v) => set.add(v));
  });
  return [...set];
}

function isIkeaMultiPackCartId(id) {
  return /^s/i.test(String(id || '').trim());
}

function dimensionsHavePackages(dimensions) {
  if (!Array.isArray(dimensions) || dimensions.length === 0) return false;
  return dimensions.some((d) => Array.isArray(d.packages) && d.packages.length > 0);
}

/** Match backend ikeaMultiPackExpansion: colli per bundle per component SKU. */
function aggregatePackageQuantitiesPerBundle(dimensions) {
  const map = new Map();
  if (!Array.isArray(dimensions)) return map;
  for (const dim of dimensions) {
    const pkgs = Array.isArray(dim.packages) ? dim.packages : [];
    for (const p of pkgs) {
      const rawSku = p.articleNumber || dim.sku;
      if (!rawSku) continue;
      const sku = String(rawSku).trim().replace(/\./g, '').replace(/\s/g, '');
      if (!sku) continue;
      const perBundle = Math.max(1, Number(p.packages != null ? p.packages : 1) || 1);
      map.set(sku, (map.get(sku) || 0) + perBundle);
    }
  }
  return map;
}

export function keysMatch(a, b) {
  if (a === b) return true;
  const na = String(a).toLowerCase();
  const nb = String(b).toLowerCase();
  if (na === nb) return true;
  const da = na.replace(/\D/g, '');
  const db = nb.replace(/\D/g, '');
  if (da && db && da === db) return true;
  const sa = da.replace(/^0+/, '') || '0';
  const sb = db.replace(/^0+/, '') || '0';
  return sa === sb;
}

export function collectCartItemKeys(item) {
  const keys = [];
  const id = item.id;
  if (id) keys.push(id);
  if (id && String(id).startsWith('s')) {
    keys.push(String(id).substring(1));
  }
  const dims = item.dimensions || [];
  dims.forEach((dim) => {
    if (dim.sku) keys.push(dim.sku);
    const pkgs = dim.packages || [];
    pkgs.forEach((p) => {
      if (p.articleNumber) keys.push(p.articleNumber);
    });
  });
  return mergeVariantKeys(keys);
}

/** Human-readable primary SKU / article for display (cart lines). */
function collectCartItemDisplaySku(item) {
  if (!item) return '';
  const id = item.id != null && String(item.id).trim() !== '' ? String(item.id).trim() : '';
  if (id) return id;
  const dims = item.dimensions || [];
  for (let i = 0; i < dims.length; i += 1) {
    const dim = dims[i];
    if (dim.sku != null && String(dim.sku).trim() !== '') {
      return String(dim.sku).trim();
    }
    const pkgs = dim.packages || [];
    for (let j = 0; j < pkgs.length; j += 1) {
      const an = pkgs[j].articleNumber;
      if (an != null && String(an).trim() !== '') {
        return String(an).trim();
      }
    }
  }
  return '';
}

export function scanMatchesLine(matchKeys, scanRaw) {
  const scanVariants = expandMatchVariants(normalizePackingScan(scanRaw));
  return matchKeys.some((mk) =>
    scanVariants.some((sv) => keysMatch(mk, sv)),
  );
}

export function resolveLineDisplayName(order, skuHint) {
  const hintVariants = expandMatchVariants(skuHint);
  const cart = order.cart || [];
  for (let i = 0; i < cart.length; i += 1) {
    const item = cart[i];
    const itemKeys = collectCartItemKeys(item);
    const hit = itemKeys.some((ik) =>
      hintVariants.some((hv) => keysMatch(ik, hv)),
    );
    if (hit) return item.name || skuHint;
  }
  return skuHint || 'Unknown SKU';
}

/**
 * Prefer deliveryNote when it has positive total quantities; otherwise cart lines.
 */
export function buildFulfillmentLines(order) {
  if (!order) return [];
  const dn = order.deliveryNote || [];
  const dnTotal = dn.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  if (dn.length > 0 && dnTotal > 0) {
    const aggregated = new Map();
    dn.forEach((row) => {
      const sku = String(row.SKU || '').trim();
      if (!sku) return;
      const amt = Number(row.amount || 0);
      const prev = aggregated.get(sku) || { SKU: sku, amount: 0 };
      prev.amount += amt;
      aggregated.set(sku, prev);
    });
    return [...aggregated.values()].map((row) => ({
      id: `dn:${row.SKU}`,
      label: resolveLineDisplayName(order, row.SKU),
      sku: String(row.SKU || '').trim(),
      total: row.amount,
      remaining: row.amount,
      matchKeys: mergeVariantKeys(row.SKU),
      source: 'deliveryNote',
    }));
  }

  const cart = order.cart || [];
  const lines = [];
  cart.forEach((item, idx) => {
    const amt = Number(item.amount || 0);
    if (amt <= 0) return;

    if (isIkeaMultiPackCartId(item.id) && dimensionsHavePackages(item.dimensions)) {
      const perBundle = aggregatePackageQuantitiesPerBundle(item.dimensions);
      const exploded = [...perBundle.entries()].filter(([, q]) => q > 0);
      const bundleEntries = exploded.filter(([, perB]) => perB * amt > 0);
      if (bundleEntries.length > 0) {
        const bundleGroupId = `mp:${String(item.id)}:${idx}`;
        const bundleLineCount = bundleEntries.length;
        bundleEntries.forEach(([sku, perB], partIdx) => {
          const total = perB * amt;
          lines.push({
            id: `cart:${item.id}:${sku}:${idx}`,
            label: item.name || item.id || `Line ${idx + 1}`,
            sku: String(sku).trim(),
            total,
            remaining: total,
            matchKeys: mergeVariantKeys(sku),
            source: 'cart · component',
            bundleGroupId,
            bundleLineCount,
            bundlePartIndex: partIdx + 1,
          });
        });
        return;
      }
    }

    lines.push({
      id: `cart:${item.id}:${idx}`,
      label: item.name || item.id || `Line ${idx + 1}`,
      sku: collectCartItemDisplaySku(item),
      total: amt,
      remaining: amt,
      matchKeys: collectCartItemKeys(item),
      source: 'cart',
    });
  });
  return lines;
}

/**
 * Groups adjacent fulfillment lines that share `bundleGroupId` (IKEA multi-package / exploded cart lines).
 * @returns {Array<{ bundleGroupId: string | null, lines: ReturnType<typeof buildFulfillmentLines> }>}
 */
export function groupFulfillmentLinesByBundle(lines) {
  if (!lines || lines.length === 0) return [];
  const groups = [];
  for (const line of lines) {
    const gid = line.bundleGroupId ?? null;
    const prev = groups[groups.length - 1];
    if (gid != null && prev && prev.bundleGroupId === gid) {
      prev.lines.push(line);
    } else {
      groups.push({ bundleGroupId: gid, lines: [line] });
    }
  }
  return groups;
}

export function findLineIndexForScan(lines, scanRaw) {
  const resolved = resolveScanMatch(lines, scanRaw);
  if (resolved.status !== 'match') return -1;
  return lines.findIndex((l) => l.id === resolved.line.id);
}

/**
 * @returns {{ status: 'empty' | 'no_match' | 'already_complete' | 'match', normalized: string, line?: object, completedMatches?: object[] }}
 */
export function resolveScanMatch(lines, scanRaw) {
  const normalized = normalizePackingScan(scanRaw);
  if (!normalized) return { status: 'empty', normalized: '' };

  const open = [];
  const completed = [];
  for (const line of lines) {
    if (!scanMatchesLine(line.matchKeys, normalized)) continue;
    if (line.remaining > 0) open.push(line);
    else completed.push(line);
  }

  if (open.length === 0) {
    if (completed.length > 0) {
      return { status: 'already_complete', normalized, completedMatches: completed };
    }
    return { status: 'no_match', normalized };
  }

  return { status: 'match', normalized, line: open[0] };
}

export function fulfillmentComplete(lines) {
  return lines.length > 0 && lines.every((l) => l.remaining <= 0);
}

/** Build checklist rows from IKEA invoice extraction ({ sku, qty, label }[]). */
export function buildFulfillmentLinesFromInvoice(rows) {
  if (!rows || rows.length === 0) return [];
  const aggregated = new Map();
  rows.forEach((row) => {
    const sku = String(row.sku || '').trim();
    if (!sku) return;
    const amt = Number(row.qty ?? row.amount ?? 0);
    if (!Number.isFinite(amt) || amt <= 0) return;
    const prev = aggregated.get(sku) || { sku, qty: 0, label: row.label || sku };
    prev.qty += amt;
    if ((!prev.label || prev.label === sku) && row.label) prev.label = row.label;
    aggregated.set(sku, prev);
  });
  return [...aggregated.values()].map((row) => ({
    id: `inv:${row.sku}`,
    label: row.label || row.sku,
    sku: row.sku,
    total: row.qty,
    remaining: row.qty,
    matchKeys: mergeVariantKeys(row.sku),
    source: 'IKEA invoice PDF',
  }));
}
